import React from 'react';
import { cardTypes } from '../../agentTheme';

function ScoreBar({ label, value, max = 20 }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const color = pct >= 80 ? '#4ade80' : pct >= 50 ? '#fbbf24' : '#f87171';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', width: 100, minWidth: 80, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)' }}>
        <div className="score-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600, width: 24, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

/**
 * Renders auto-analysis results as a visual score card.
 */
export default function AnalysisResultCard({ results, onDismiss }) {
  if (!results?.length) return null;

  return (
    <div className="smart-card" style={{
      background: cardTypes.analysis.bg,
      border: `1px solid ${cardTypes.analysis.border}`,
      animation: 'messageIn 0.3s ease forwards',
    }}>
      <div className="smart-card-header" style={{ color: cardTypes.analysis.accent }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
        </svg>
        Quality Analysis
        {onDismiss && (
          <button
            onClick={onDismiss}
            aria-label="Dismiss analysis"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 14 }}
          >
            ✕
          </button>
        )}
      </div>

      {results.map((r, i) => {
        const score = r.score;
        const overall = score?.overall;
        const overallColor = overall == null ? 'rgba(255,255,255,0.3)' : overall >= 70 ? '#4ade80' : overall >= 40 ? '#fbbf24' : '#f87171';

        return (
          <div key={i} style={{ marginTop: i > 0 ? 12 : 10, paddingTop: i > 0 ? 12 : 0, borderTop: i > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {r.imageUrl && (
                <img src={r.imageUrl} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }} />
              )}
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: overallColor }}>{overall ?? '?'}</span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>/100</span>
                </div>
              </div>
            </div>

            {score && (
              <div style={{ marginTop: 8 }}>
                {score.prompt_adherence != null && <ScoreBar label="Prompt Adherence" value={score.prompt_adherence} />}
                {score.visual_impact != null && <ScoreBar label="Visual Impact" value={score.visual_impact} />}
                {score.brand_consistency != null && <ScoreBar label="Brand Consistency" value={score.brand_consistency} />}
                {score.professional_quality != null && <ScoreBar label="Quality" value={score.professional_quality} />}
                {score.marketing_effectiveness != null && <ScoreBar label="Effectiveness" value={score.marketing_effectiveness} />}
              </div>
            )}

            {score?.strengths && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 8, lineHeight: 1.5 }}>
                <span style={{ color: '#4ade80' }}>+</span> {score.strengths}
              </div>
            )}
            {score?.issues && score.issues !== 'none' && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2, lineHeight: 1.5 }}>
                <span style={{ color: '#f87171' }}>-</span> {score.issues}
              </div>
            )}
            {score?.suggestion && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 2, fontStyle: 'italic', lineHeight: 1.5 }}>
                {score.suggestion}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
